import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Euro, Clock, Package, Lock, Crown, AlertTriangle, Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function PackageForm({ pkg, onSave, onClose }) {
  const [form, setForm] = useState({
    name: pkg?.name || "",
    description: pkg?.description || "",
    amount: pkg?.amount || "",
    currency: pkg?.currency || "eur",
    duration: pkg?.duration || "",
    is_active: pkg?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) {
      toast.error("Nom et prix sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        amount: parseFloat(form.amount),
        duration: form.duration ? parseInt(form.duration) : null,
      });
      onClose();
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Nom du forfait</Label>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Ex: Pack Coaching 3 mois"
        />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Décrivez ce que comprend ce forfait..."
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Prix (€)</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="49.99"
          />
        </div>
        <div>
          <Label htmlFor="duration">Durée (jours)</Label>
          <Input
            id="duration"
            type="number"
            min="1"
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            placeholder="30"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch
          checked={form.is_active}
          onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
        />
        <Label>Forfait actif (visible par les élèves)</Label>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
          Annuler
        </Button>
        <Button type="submit" disabled={saving} className="flex-1 rounded-full">
          {saving ? "Sauvegarde..." : pkg ? "Modifier" : "Créer"}
        </Button>
      </div>
    </form>
  );
}

export default function CoachPackages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [maxPackages, setMaxPackages] = useState(null);
  const [currentCount, setCurrentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPkg, setEditPkg] = useState(null);

  // AI state
  const [showAi, setShowAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [aiCreating, setAiCreating] = useState(false);

  const isPro = user?.subscription?.plan_type === 'pro';

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const { data } = await api.get("/coach-packages");
      setPackages(data.packages || []);
      setMaxPackages(data.max_packages ?? null);
      setCurrentCount(data.current_count ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isAtLimit = maxPackages !== null && currentCount >= maxPackages;
  const hasNoSubscription = maxPackages === 0;

  const handleCreate = async (form) => {
    await api.post("/coach-packages", form);
    toast.success("Forfait créé !");
    loadPackages();
  };

  const handleUpdate = async (form) => {
    await api.put(`/coach-packages/${editPkg.package_id}`, form);
    toast.success("Forfait modifié !");
    loadPackages();
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm("Supprimer ce forfait ?")) return;
    try {
      await api.delete(`/coach-packages/${packageId}`);
      toast.success("Forfait supprimé");
      loadPackages();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openCreate = () => {
    setEditPkg(null);
    setDialogOpen(true);
  };

  const openEdit = (pkg) => {
    setEditPkg(pkg);
    setDialogOpen(true);
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiResults(null);
    try {
      const res = await api.post('/ai/generate-packages', { prompt: aiPrompt });
      const generated = res.data.packages || [];
      if (generated.length === 0) {
        toast.error("L'IA n'a généré aucun forfait");
        return;
      }
      setAiResults(generated);
      toast.success(`${generated.length} forfait(s) généré(s) ! Vérifiez puis confirmez.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération IA");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiConfirm = async () => {
    if (!aiResults?.length) return;
    setAiCreating(true);
    let created = 0;
    try {
      for (const pkg of aiResults) {
        await api.post("/coach-packages", {
          name: pkg.name,
          description: pkg.description || "",
          amount: parseFloat(pkg.amount),
          duration: pkg.duration ? parseInt(pkg.duration) : null,
          is_active: true,
        });
        created++;
      }
      toast.success(`${created} forfait(s) créé(s) avec succès !`);
      setAiResults(null);
      setAiPrompt("");
      setShowAi(false);
      loadPackages();
    } catch (err) {
      toast.error(`${created} créé(s), erreur sur le reste`);
      loadPackages();
    } finally {
      setAiCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Pas d'abonnement actif
  if (!loading && hasNoSubscription) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900">
            Mes Tarifs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les forfaits proposés à vos élèves
          </p>
        </div>
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Abonnement requis</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Vous devez avoir un abonnement actif pour créer des forfaits pour vos élèves.
            </p>
            <Button onClick={() => navigate("/coach/subscription")} className="rounded-full gap-2">
              <Crown className="w-4 h-4" /> Voir les abonnements
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900">
            Mes Tarifs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les forfaits proposés à vos élèves
            {maxPackages !== null && maxPackages > 0 && (
              <span className="ml-2 text-xs font-medium text-slate-400">
                ({currentCount}/{maxPackages} forfaits)
              </span>
            )}
            {maxPackages === null && (
              <span className="ml-2 text-xs font-medium text-green-500">
                (illimité)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isPro && (
            <Button
              variant="outline"
              onClick={() => { setShowAi(!showAi); setAiResults(null); }}
              className="rounded-full gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
            >
              <Sparkles className="w-4 h-4" /> Générer avec IA
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                disabled={isAtLimit}
                className="rounded-full gap-2"
              >
                <Plus className="w-4 h-4" /> Nouveau forfait
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editPkg ? "Modifier le forfait" : "Nouveau forfait"}</DialogTitle>
              </DialogHeader>
              <PackageForm
                pkg={editPkg}
                onSave={editPkg ? handleUpdate : handleCreate}
                onClose={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Generation Panel */}
      {showAi && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-purple-900">Génération de forfaits par IA</span>
              <Badge className="bg-purple-100 text-purple-700 text-[10px]">PRO</Badge>
            </div>

            {!aiResults ? (
              <>
                <Textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder={"Décrivez les forfaits que vous souhaitez créer...\nEx: 5 forfaits coaching, du basique au premium, dont 1 spécial prépa concours bodybuilding, durées de 1 à 6 mois"}
                  className="bg-white min-h-[100px] text-sm"
                />
                <Button
                  disabled={aiLoading || !aiPrompt.trim()}
                  onClick={handleAiGenerate}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-2 w-full"
                >
                  {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération en cours...</> : <><Sparkles className="w-4 h-4" /> Générer les forfaits</>}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-purple-700 font-medium">{aiResults.length} forfait(s) généré(s) — vérifiez puis confirmez :</p>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {aiResults.map((pkg, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border border-purple-100 flex items-start gap-4">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Euro className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 text-sm">{pkg.name}</h4>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{pkg.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="secondary" className="text-xs">{parseFloat(pkg.amount).toFixed(2)} €</Badge>
                          {pkg.duration && <Badge variant="outline" className="text-xs">{pkg.duration} jours</Badge>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="flex-shrink-0 h-8 w-8"
                        onClick={() => setAiResults(prev => prev.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setAiResults(null)}
                    className="flex-1 rounded-full"
                  >
                    Recommencer
                  </Button>
                  <Button
                    disabled={aiCreating || aiResults.length === 0}
                    onClick={handleAiConfirm}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-2"
                  >
                    {aiCreating ? <><Loader2 className="w-4 h-4 animate-spin" /> Création...</> : <><Check className="w-4 h-4" /> Créer {aiResults.length} forfait(s)</>}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bannière limite atteinte */}
      {isAtLimit && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">
                Limite de {maxPackages} forfaits atteinte
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Passez au plan Pro pour créer un nombre illimité de forfaits.
              </p>
            </div>
            <Button
              onClick={() => navigate("/coach/subscription")}
              variant="outline"
              size="sm"
              className="rounded-full text-orange-600 border-orange-300 hover:bg-orange-100"
            >
              <Crown className="w-3.5 h-3.5 mr-1.5" /> Passer Pro
            </Button>
          </CardContent>
        </Card>
      )}

      {packages.length === 0 && !showAi ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucun forfait</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Créez votre premier forfait pour que vos élèves puissent vous payer
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={openCreate} className="rounded-full gap-2">
                <Plus className="w-4 h-4" /> Créer un forfait
              </Button>
              {isPro && (
                <Button variant="outline" onClick={() => setShowAi(true)} className="rounded-full gap-2 text-purple-600 border-purple-200 hover:bg-purple-50">
                  <Sparkles className="w-4 h-4" /> Générer avec IA
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {packages.map((pkg) => (
            <Card key={pkg.package_id} className={`transition-shadow hover:shadow-md ${!pkg.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Euro className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 truncate">{pkg.name}</h3>
                    {!pkg.is_active && (
                      <Badge variant="outline" className="text-slate-400 border-slate-300">
                        Inactif
                      </Badge>
                    )}
                  </div>
                  {pkg.description && (
                    <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{pkg.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1">
                    {pkg.duration && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {pkg.duration} jours
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-bold text-slate-900">{pkg.amount.toFixed(2)}€</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(pkg)}>
                    <Pencil className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(pkg.package_id)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
